import React, { useCallback, useMemo } from 'react';
import { NextPage } from 'next';
import {
  Button, ButtonColor, ButtonSize, ButtonVariant, IframeWrapper, Spacing, SpacingName, Typography,
} from '@doveit/lego';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import Hero, { HeroProps } from '../client/templates/common/hero/Hero';
import { importDynamicData } from '../services/importDynamicData';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import {
  EDUCATION_QUIZ_TEST_AS,
  EDUCATION_QUIZ_TEST_URL,
  goToPage,
  JOIN_OUR_AGENTS_AS,
  JOIN_OUR_AGENTS_URL,
  WORK_WITH_US_AS,
  WORK_WITH_US_URL,
} from '../client/utils/links';
import { requireResourcesFromStaticAssets } from '../client/utils/assetsUtils';
import PageSection from '../client/components/page-section/PageSection';
import { TextAlign, Video, WrapperSize } from '../client/types';
import * as styles from '../client/templates/education-quiz-intro/EducationQuizIntro.style';
import { FILE_DOCUMENT_OUTLINE } from '../client/themes/icons';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { GUIDES_BY_IDS } from '../client/graphql/queries/guide';
import {
  Article, Guide, AdvFluidType, Department, JobOffer,
} from '../domain/graphcms';
import { ArticleType } from '../domain/articleType';
import { mapArticles } from '../client/graphql/mappers/articleMappers';
import CardsHList from '../client/components/cards-hlist/CardsHList';
import ArticleVerticalCard from '../client/components/article-vertical-card/ArticleVerticalCard';
import LogosSection, { LogosSectionProps } from '../client/templates/common/logos-section/LogosSection';
import { getAdvByType } from '../services/advs/advs';
import { Adv } from '../domain/adv';
import AdvFluid from '../client/components/adv-fluid/AdvFluid';
import SectionDivider from '../client/components/section-divider/SectionDivider';
import Markdown from '../client/components/markdown/Markdown';
import { LATEST_JOB_OFFER_BY_DEPARTMENT } from '../client/graphql/queries/job-offer';
import JobOffers from '../client/components/job-offer-mini-card-list/JobOffers';

export interface EducationQuizIntroProps {
  SEO: PageHeadProps['SEO'],
  hero: HeroProps & {
    secondarySlotAction: string,
    secondarySlotDescription: string,
  },
  intro: {
    title: string,
    description: string,
    videoTitle: string,
    video: Video,
    caption: string,
    cta: string,
  },
  education: {
    title: string,
    articles: Article[],
  },
  jobOffers: {
    title: string,
    items: JobOffer[],
    cta: string,
  },
  press: LogosSectionProps,
  adv: Adv,
}

export const EducationQuizIntro: NextPage<EducationQuizIntroProps> = ({
  SEO,
  hero,
  intro,
  education,
  jobOffers,
  press,
  adv,
}) => {
  const goToQuiz = useCallback(
    () => goToPage({
      url: EDUCATION_QUIZ_TEST_URL,
      as: EDUCATION_QUIZ_TEST_AS,
    }),
    [],
  );

  const goToBecameAgent = useCallback(
    () => goToPage({
      url: JOIN_OUR_AGENTS_URL,
      as: JOIN_OUR_AGENTS_AS,
    }),
    [],
  );

  const goToWorkWithUs = useCallback(
    () => goToPage({
      url: WORK_WITH_US_URL,
      as: WORK_WITH_US_AS,
    }),
    [],
  );

  const educationArticles = useMemo(() => mapArticles(education.articles, ArticleType.EDUCATION), [education.articles]);

  return (
    <AppLayout activePage="education-quiz-intro">
      <PageHead SEO={SEO} />
      <PageLayout>
        <Hero
          title={hero.title}
          description={hero.description}
          backgroundImage={requireResourcesFromStaticAssets(hero.backgroundImage)}
          secondarySlotTitle={hero.secondarySlotTitle}
          secondarySlot={(
            <>
              <Markdown>
                {hero.secondarySlotDescription}
              </Markdown>
              <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                <Button
                  dataRef="hero-start-quiz"
                  label={hero.secondarySlotAction}
                  onClick={goToQuiz}
                />
              </Spacing>
            </>
          )}
        />
        <PageSection>
          <PageSection.Wrapper>
            <div css={styles.grid}>
              <div css={styles.column}>
                <PageSection.Title>
                  {intro.title}
                </PageSection.Title>
                <PageSection.Description text={intro.description} />
                <Spacing margin={[SpacingName.SMALL, 0]}>
                  <Typography.H4>{intro.caption}</Typography.H4>
                </Spacing>
                <Button
                  dataRef="intro-start-quiz"
                  label={intro.cta}
                  onClick={goToQuiz}
                />
              </div>
              <div css={styles.column}>
                <Spacing margin={[0, 0, SpacingName.SMALL]}>
                  <Typography.H3>{intro.videoTitle}</Typography.H3>
                </Spacing>
                <div css={styles.videoWrapper}>
                  <IframeWrapper
                    title={intro.video.title}
                    src={intro.video.embedUrl}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    fit
                  />
                </div>
                <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                  <Button
                    dataRef="intro-send-application"
                    label="Invia la tua candidatura"
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.OUTLINE}
                    color={ButtonColor.PRIMARY}
                    icon={FILE_DOCUMENT_OUTLINE}
                    onClick={goToBecameAgent}
                  />
                </Spacing>
              </div>
            </div>
          </PageSection.Wrapper>
        </PageSection>
        <SectionDivider />
        <PageSection>
          <PageSection.Wrapper>
            <PageSection.Title textAlign={TextAlign.CENTER}>
              {education.title}
            </PageSection.Title>
          </PageSection.Wrapper>
          <PageSection.Wrapper withoutPad>
            <PageSection.Block>
              <CardsHList inline={3} sideSpacing={SpacingName.SMALL}>
                {educationArticles.map((article) => (
                  <CardsHList.Item key={article.id}>
                    <ArticleVerticalCard {...article} />
                  </CardsHList.Item>
                ))}
              </CardsHList>
            </PageSection.Block>
          </PageSection.Wrapper>
        </PageSection>
        <SectionDivider />
        <PageSection>
          <PageSection.Wrapper maxWidth={WrapperSize.M}>
            <AdvFluid
              {...adv}
            />
          </PageSection.Wrapper>
        </PageSection>
        {jobOffers.items.length > 0 ? (
          <PageSection data-ref="job-offers-section" backgroundColor="neutrals.lighter">
            <PageSection.Wrapper>
              <PageSection.Title textAlign={TextAlign.CENTER}>
                {jobOffers.title}
              </PageSection.Title>
              <PageSection.Block>
                <div css={styles.offersWrapper}>
                  <JobOffers
                    items={jobOffers.items}
                  />
                </div>
              </PageSection.Block>
              <PageSection.Actions textAlign={TextAlign.CENTER}>
                <Button
                  dataRef="job-offers-cta"
                  label={jobOffers.cta}
                  onClick={goToWorkWithUs}
                />
              </PageSection.Actions>
            </PageSection.Wrapper>
          </PageSection>
        ) : <SectionDivider />}
        <LogosSection
          title={press.title}
          items={press.items}
          inlineItems={6}
        />
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError(async () => {
  const graphQLClient = initGraphQLClient();
  const [press, pageData, adv, { data: { jobOffers } }] = await Promise.all([
    importDynamicData('press.yml'),
    importDynamicData('pages/education-quiz-intro.yml'),
    getAdvByType(AdvFluidType.AGENT_JOB_APPLICATION),
    graphQLClient.query<{ jobOffers: JobOffer[] }>({
      query: LATEST_JOB_OFFER_BY_DEPARTMENT,
      variables: {
        departments: [
          Department.OPERATIONS,
          Department.SALES,
        ],
        first: 4,
      },
    }),
  ]);

  const { data: { guides } } = await graphQLClient.query<{ guides: Guide[] }>({
    query: GUIDES_BY_IDS,
    variables: { ids: pageData.education.articleIds },
  });

  return {
    props: {
      ...pageData,
      education: {
        ...pageData.education,
        articles: guides,
      },
      jobOffers: {
        ...pageData.jobOffers,
        items: jobOffers,
      },
      press: {
        ...pageData.press,
        items: press.items,
      },
      adv,
    },
  };
});

export default withError(EducationQuizIntro);
